import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Paper, TextField, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { Badge } from '@components';
import { Trans, useTrans } from '@libs/i18n';
import { ArrowLeftIcon, CheckIcon, PaperPlaneTiltIcon, PencilIcon } from '@phosphor-icons/react';

interface SummaryHeaderProps {
  assessmentTitle: string;
  onSubmitClick: () => void;
  onTitleUpdate?: (newTitle: string) => void;
  assessmentLanguage?: {
    code: string;
    label: string;
  };
}

export const SummaryHeader: FC<SummaryHeaderProps> = observer(
  ({ assessmentTitle, onSubmitClick, onTitleUpdate, assessmentLanguage }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const navigate = useNavigate();
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState(assessmentTitle);

    const handleBack = () => {
      navigate(routes.assessmentList());
    };

    const handleEditTitle = () => {
      setIsEditingTitle(true);
      setEditedTitle(assessmentTitle);
    };

    const handleSaveTitle = () => {
      if (editedTitle.trim() && editedTitle !== assessmentTitle && onTitleUpdate) {
        onTitleUpdate(editedTitle.trim());
      }
      setIsEditingTitle(false);
    };

    const handleCancelEdit = () => {
      setIsEditingTitle(false);
      setEditedTitle(assessmentTitle);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSaveTitle();
      } else if (e.key === 'Escape') {
        handleCancelEdit();
      }
    };

    return (
      <Paper
        variant="panel"
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.grey[300]}`, px: 5 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Left section - Back button and title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            {/* Back button */}
            <Button
              startIcon={<ArrowLeftIcon size={24} />}
              onClick={handleBack}
              variant="text"
              sx={{
                color: 'text.primary',
                textTransform: 'none',
                px: 2,
                py: 1,
                backgroundColor: 'transparent',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Typography variant="textMD_semiBold">
                <Trans i18nKey="assessments.summary.back" />
              </Typography>
            </Button>

            {/* Title with edit functionality */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              {isEditingTitle ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleSaveTitle}
                    autoFocus
                    size="small"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleSaveTitle}
                    sx={{
                      color: 'accent.green',
                      '&:hover': {
                        backgroundColor: 'green.900',
                      },
                    }}
                  >
                    <CheckIcon size={20} weight="bold" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="textMD_semiBold"
                    sx={{
                      color: 'secondary.main',
                    }}
                  >
                    {assessmentTitle}
                  </Typography>

                  {assessmentLanguage && (
                    <Badge
                      variant="Language"
                      label={assessmentLanguage.label}
                      sx={{
                        height: '24px',
                        textTransform: 'none',
                        backgroundColor: '#F2F2F2',
                        color: 'grey.700',
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        px: 1,
                        ml: 1,
                      }}
                    />
                  )}

                  {onTitleUpdate && (
                    <IconButton
                      size="small"
                      onClick={handleEditTitle}
                      aria-label={t('app.assessments.ariaLabel.editTitle')}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <PencilIcon size={16} />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* Right section - Submit button */}
          <Button
            variant="primary"
            onClick={onSubmitClick}
            endIcon={<PaperPlaneTiltIcon size={20} />}
            sx={{
              px: 5,
              py: 1.5,
              gap: 1,
            }}
          >
            <Typography variant="textMD_semiBold" sx={{ color: 'white' }}>
              <Trans i18nKey="assessments.summary.submitAssessment" />
            </Typography>
          </Button>
        </Box>
      </Paper>
    );
  },
);
